"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { PropertyTypeV2 } from "commonModule/services/common";
import useCurrency from "hooks/swr/useCurrency";
import { markBookingStore } from "stores/booking/bookingSlice";
import { useAppDispatch } from "stores/hooks";
import BookingActions from "./blocks/BookingActions";
import BookingPriceHeader from "./blocks/BookingPriceHeader";
import ClientViewBlock from "./blocks/ClientViewBlock";
import CommissionRateModal from "./blocks/CommissionRateModal";
import DateGuestSelector from "./blocks/DateGuestSelector";
import DetailsToggleButton from "./blocks/DetailsToggleButton";
import PriceBreakdownBlock from "./blocks/PriceBreakdownBlock";
import { useBookingSummary } from "./blocks/useBookingSummary";
import { BookingPriceLoading } from "./BookingPriceLoading";
import NotLoggedInBookingState from "./NotLoggedInBookingState";
import { useBookingSectionController } from "./useBookingSectionController";
const BookingStickyMobile = ({
  property
}: {
  property: PropertyTypeV2;
}) => {
  const dispatch = useAppDispatch();
  const [isMounted, setIsMounted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRateModalOpen, setIsRateModalOpen] = useState(false);
  const {
    formatter,
    rate: fxRate
  } = useCurrency();
  const {
    bookingStore,
    isLoggedIn,
    isAgent,
    isTravelAgentClient,
    isAuthPending,
    loaderDots,
    translateBookBtn,
    onBookClick,
    openCalendar
  } = useBookingSectionController({
    property
  });
  const {
    derivedCommissionRate
  } = useBookingSummary();
  useEffect(() => {
    setIsMounted(true);
  }, []);
  if (!isMounted) return null;
  const showLoginState = !isLoggedIn && !isTravelAgentClient;
  const showNotEligibleState = isLoggedIn && !isAgent && !isTravelAgentClient;
  const showClientView = isTravelAgentClient && !isAgent;
  const canShowBreakdown = bookingStore.hasPrequote && !!bookingStore.startDate && !!bookingStore.endDate && bookingStore.isDateAvailable;
  return createPortal(<>
      <div className='fixed bottom-0 left-0 z-40 w-full'>
        <div className='relative flex w-full flex-col gap-4 rounded-t-[16px] bg-white px-4 pb-2 pt-4 shadow-[0_-4px_9px_0_rgba(34,34,34,0.14)]'>
          {isAuthPending && <BookingPriceLoading />}

          {!isAuthPending && showLoginState && <NotLoggedInBookingState variant='login' />}
          {!isAuthPending && showNotEligibleState && <NotLoggedInBookingState variant='not-eligible' />}

          {!isAuthPending && (isAgent || showClientView) && bookingStore.isLoading && <BookingPriceLoading />}

          {!isAuthPending && (isAgent || showClientView) && !bookingStore.isLoading && (!canShowBreakdown ? <div className='flex flex-col gap-3'>
                <p className='text-[16px] font-bold leading-none text-textColor'>
                  {formatter(0, 0).format((property?.min_price || 0) * fxRate)}
                  <span className='ml-1 text-[12px] font-medium text-Gray-700'>
                    avg/night
                  </span>
                </p>
                <DateGuestSelector property={property} onDateClick={openCalendar} />
                <BookingActions propertyId={property.property_id} bookLabel={translateBookBtn} loaderDots={loaderDots} onBookClick={onBookClick} layout='inline' showShare={false} />
              </div> : <div className='flex flex-col gap-4'>
                <BookingPriceHeader size='sticky' isTravelAgentClient={showClientView} dateGuestSelector={<DateGuestSelector property={property} onDateClick={openCalendar} />} trailingAction={isAgent ? <DetailsToggleButton expanded={isExpanded} onClick={() => setIsExpanded(v => !v)} /> : null} />

                {isAgent && isExpanded && <div className='flex max-h-[50vh] flex-col gap-4 overflow-y-auto px-2'>
                    <div className='h-[1px] w-full bg-Gray-200' />
                    <PriceBreakdownBlock onChangeRate={() => setIsRateModalOpen(true)} />
                    <div className='h-[1px] w-full bg-Gray-200' />
                    <ClientViewBlock />
                  </div>}

                {showClientView && <div className='h-[1px] w-full bg-Gray-200' />}

                <BookingActions propertyId={property.property_id} bookLabel='Book now' loaderDots={loaderDots} onBookClick={onBookClick} layout='inline' showShare={isAgent} />
                {showClientView && <p className='text-center text-[13px] font-medium leading-none tracking-[-0.13px] text-Gray-800'>
                    You won&apos;t be charged yet
                  </p>}
              </div>)}
        </div>
      </div>
      {isAgent && <CommissionRateModal isOpen={isRateModalOpen} currentRate={derivedCommissionRate} onClose={() => setIsRateModalOpen(false)} onConfirm={next => {
      dispatch(markBookingStore({
        commissionRatePercent: next
      }));
      setIsRateModalOpen(false);
    }} />}
    </>, document.body);
};
export default BookingStickyMobile;